import { createNewChat } from 'actions/supportAssistant/supportAssistantActions'
import HeadData from 'components/Common/HeadData'
import LayoutContainer from 'components/Common/LayoutContainer'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import TextButton from 'components/Luxkit/Button/TextButton'
import Divider from 'components/Luxkit/Divider'
import LinePlusIcon from 'components/Luxkit/Icons/line/LinePlusIcon'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import Group from 'components/utils/Group'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import zIndex from 'styles/tools/z-index'
import ChatHeader from 'supportAssistant/components/Chat/ChatHeader'
import MessageInput from 'supportAssistant/components/Chat/MessageInput'
import MessagesListContainer from 'supportAssistant/components/Chat/MessagesListContainer'
import useChatState from 'supportAssistant/hooks/useChatState'
import useTabVisibility from 'hooks/useTabVisibility'
import { selectCurrentChat, selectOutageMessage } from 'supportAssistant/selectors/selectCurrentChat'
import Agent from '../Support/Agent'
import MetaComponents from './MessageMeta/MetaComponents'

const Container = styled(Group)`
  min-height: 100dvh;
`

const Body = styled(LayoutContainer)`
  flex-grow: 1;
`

const Footer = styled(LayoutContainer)`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  position: sticky;
  bottom: 0;
  padding-bottom: ${rem(16)};
  z-index: ${zIndex.stickyFooter};
`

const StartNewChatContainer = styled(Group)`
  padding-left: ${rem(32)};
`

const ActionsSkeleton = styled(LoadingBox)`
  align-self: start;
  height: ${rem(40)};
  margin-top: ${rem(12)};
  margin-left: ${rem(32)};
`

const ScrollTo = styled.div`
  padding-bottom: ${rem(24)};
`

const HeaderContainer = styled(LayoutContainer)`
  position: sticky;
  top: 0;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  z-index: ${zIndex.stickyHeader};
`

function Chat() {
  const [messages, typing, agentTyping, fetchingChatMessages] = useChatState()

  const dispatch = useAppDispatch()
  const isTabVisible = useTabVisibility()

  const currentChat = useAppSelector(state => selectCurrentChat(state))
  const outageMessage = useAppSelector(state => selectOutageMessage(state))
  const [showDocumentTitleMessage, setShowDocumentTitleMessage] = useState(false)

  const lastMessage = messages[messages.length - 1]
  const isLastMessageByUser = lastMessage?.sender === 'user'

  const showChatActions = !fetchingChatMessages && lastMessage && currentChat?.status !== 'CLOSED' && lastMessage.sender === 'chatbot'

  const handleNewChatClick = useCallback(() => {
    dispatch(createNewChat())
  }, [dispatch])

  const scrollRef = useRef<HTMLInputElement>(null)

  const scrollToBottom = () => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'end' })
  }

  useEffect(() => {
    scrollToBottom()
  })

  const lastSeenMessage = useRef(lastMessage)

  useEffect(() => {
    if (!isTabVisible && lastSeenMessage?.current?.id !== lastMessage?.id) {
      setShowDocumentTitleMessage(true)
      lastSeenMessage.current = lastMessage
    }
  }, [isTabVisible, lastMessage, messages.length])

  useEffect(() => {
    if (isLastMessageByUser || isTabVisible) {
      setShowDocumentTitleMessage(false)
    }
  }, [isLastMessageByUser, isTabVisible, messages])

  return <Container direction="vertical">
    <HeaderContainer>
      <ChatHeader />
      {showDocumentTitleMessage && <HeadData defer={false} title="(1) New Message - Luxury Escapes" />}
      <Divider direction="horizontal" kind="primary" />
    </HeaderContainer>
    <Body size="md">
      <Group direction="vertical" verticalAlign="start" gap={16}>
        {fetchingChatMessages && <ActionsSkeleton />}
        {!fetchingChatMessages && <MessagesListContainer
          loadingMessages={fetchingChatMessages}
          messages={messages}
          isTyping={typing}
          isAgentTyping={agentTyping}
        />}
        {showChatActions && lastMessage?.meta?.content && <MetaComponents
          metaContent={lastMessage.meta.content}
          payload={lastMessage?.meta?.payload}
        />}
        {!fetchingChatMessages && currentChat?.status === 'CLOSED' && (
          <StartNewChatContainer fullWidth horizontalAlign="start" direction="vertical" gap={8}>
            <BodyTextBlock variant="small">
              This chat is timed out. Please start a new chat.
            </BodyTextBlock>

            <TextButton
              kind="secondary"
              variant="dark"
              onClick={handleNewChatClick}
              startIcon={<LinePlusIcon />}
            >
              Start new chat
            </TextButton>
          </StartNewChatContainer>
        )}
        <Agent loadingMessages={fetchingChatMessages} />
        <ScrollTo ref={scrollRef} />

        {!fetchingChatMessages && outageMessage && (
          <MessageBanner
            description={outageMessage}
            heading=""
            kind="critical"
          />
        )}
      </Group>
    </Body>
    <Footer size="md">
      {fetchingChatMessages && <LoadingBox style={{ height: rem(44), width: '100%' }} />}
      {!fetchingChatMessages && <MessageInput isTyping={typing} />}
    </Footer>
  </Container>
}

export default Chat
